/* Preview-only — 5 logo concepts rendered live so the user can compare before choosing. */
import { useEffect, useState } from "react";

const Card = ({ id, name, vibe, children, dark = false }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.18em] text-neutral-400">
      <span>{id}</span>
      <span>{vibe}</span>
    </div>
    <h3 className="mt-2 text-base font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      {name}
    </h3>
    <div className={"mt-6 flex h-40 items-center justify-center rounded-xl " + (dark ? "bg-black" : "bg-neutral-50")}>
      {children}
    </div>
  </div>
);

const Wordmark = ({ color = "#0A8B85", className = "" }) => (
  <span
    className={"text-[44px] font-extrabold tracking-tight " + className}
    style={{ fontFamily: "Manrope, sans-serif", color, letterSpacing: "-0.03em" }}
  >
    Clinq
  </span>
);

/* A — Interlocking Cs mark */
const LogoA = () => (
  <div className="flex items-center gap-3">
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
      <path d="M36 11a18 18 0 1 0 0 30" stroke="#0A8B85" strokeWidth="6" strokeLinecap="round"/>
      <path d="M16 41a18 18 0 1 0 0-30" stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" opacity="0.95"/>
    </svg>
    <Wordmark />
  </div>
);

/* B — Pulse above the "i" tittle (matches shared ClinqLogo) */
const LogoB = () => (
  <div>
    <span className="text-[44px] font-extrabold tracking-tight text-[#0A8B85]"
          style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em" }}>
      Cl
    </span>
    <span className="relative inline-block text-[44px] font-extrabold tracking-tight text-[#0A8B85]"
          style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em" }}>
      {"\u0131"}
      <span className="logo-b-dot absolute left-1/2 h-3 w-3 rounded-full bg-[#F59E0B]"
            style={{ bottom: "1.05em" }}/>
    </span>
    <span className="text-[44px] font-extrabold tracking-tight text-[#0A8B85]"
          style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em" }}>
      nq
    </span>
    <style>{`
      .logo-b-dot { box-shadow: 0 0 0 0 rgba(245,158,11,0.6); animation: lbdot 2.4s ease-out infinite; }
      @keyframes lbdot {
        0%   { box-shadow: 0 0 0 0   rgba(245,158,11,0.6); transform: translateX(-50%) scale(1);    }
        70%  { box-shadow: 0 0 0 14px rgba(245,158,11,0);  transform: translateX(-50%) scale(1.18); }
        100% { box-shadow: 0 0 0 0   rgba(245,158,11,0);   transform: translateX(-50%) scale(1);    }
      }
    `}</style>
  </div>
);

/* C — African sunrise underline */
const LogoC = () => (
  <div className="flex flex-col items-center">
    <Wordmark />
    <span className="mt-1 h-1.5 w-[150px] rounded-full"
          style={{ background: "linear-gradient(90deg, #0A8B85 0%, #F59E0B 60%, #C2410C 100%)" }}/>
  </div>
);

/* D — Bracketed wordmark */
const LogoD = () => (
  <div className="flex items-center gap-2">
    <span className="text-[44px] font-extrabold text-[#F59E0B]"
          style={{ fontFamily: "Manrope, sans-serif" }}>[</span>
    <Wordmark />
    <span className="text-[44px] font-extrabold text-[#F59E0B]"
          style={{ fontFamily: "Manrope, sans-serif" }}>]</span>
  </div>
);

/* E — Custom Cq monogram with arrow descender */
const LogoE = () => (
  <div className="flex items-center gap-3">
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      {/* C */}
      <path d="M37 13a16 16 0 1 0 0 28" stroke="#0A8B85" strokeWidth="6" strokeLinecap="round"/>
      {/* q circle */}
      <circle cx="40" cy="27" r="9" stroke="#F59E0B" strokeWidth="4"/>
      {/* arrow descender */}
      <path d="M46 33l8 12M50 41l4 4-2 6" stroke="#F59E0B" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
    <Wordmark />
  </div>
);

/* C + B combo — sunrise underline + dot Q */
const LogoCB = () => (
  <div className="flex flex-col items-center">
    <div className="relative">
      <span className="text-[44px] font-extrabold tracking-tight text-[#0A8B85]"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em" }}>
        Clin
      </span>
      <span className="relative inline-block text-[44px] font-extrabold tracking-tight text-[#0A8B85]"
            style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em" }}>
        q
        <span className="logo-b-dot absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-[#F59E0B]"/>
      </span>
    </div>
    <span className="mt-1 h-1.5 w-[150px] rounded-full"
          style={{ background: "linear-gradient(90deg, #0A8B85 0%, #F59E0B 60%, #C2410C 100%)" }}/>
  </div>
);

/* Dark navbar preview band */
const NavPreview = ({ children, label }) => (
  <div className="rounded-2xl bg-black px-6 py-5">
    <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{label}</p>
    {children}
  </div>
);

/* Compact (navbar-size) variants */
const Mini = ({ Component }) => (
  <div style={{ transform: "scale(0.55)", transformOrigin: "left center" }}>
    <Component />
  </div>
);

export default function LogoPreview() {
  const [page] = useState(1);
  useEffect(() => { document.title = "Clinq — Logo Concepts"; }, []);
  return (
    <div className="min-h-screen bg-[#F4F8F8] px-6 py-12 text-neutral-900">
      <div className="mx-auto max-w-6xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Internal preview</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          Clinq — 5 logo concepts
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Live SVG/CSS, no image deps. Each variant is shown in its hero state and again on a black navbar.
        </p>

        {/* Hero-size cards */}
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Card id="A" name="Interlocking Cs" vibe="Institutional"><LogoA /></Card>
          <Card id="B" name="Dot Q (pulse)" vibe="Modern fintech"><LogoB /></Card>
          <Card id="C" name="Sunrise underline" vibe="Pan-African"><LogoC /></Card>
          <Card id="D" name="[ Bracketed ]" vibe="Capital markets"><LogoD /></Card>
          <Card id="E" name="Cq monogram + arrow" vibe="Ownable mark"><LogoE /></Card>
          <Card id="C+B" name="Sunrise + Dot Q" vibe="My pick"><LogoCB /></Card>
        </div>

        {/* Black navbar previews */}
        <h2 className="mt-16 text-xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          On a dark navbar
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <NavPreview label="A"><Mini Component={LogoA} /></NavPreview>
          <NavPreview label="B"><Mini Component={LogoB} /></NavPreview>
          <NavPreview label="C"><Mini Component={LogoC} /></NavPreview>
          <NavPreview label="D"><Mini Component={LogoD} /></NavPreview>
          <NavPreview label="E"><Mini Component={LogoE} /></NavPreview>
          <NavPreview label="C+B"><Mini Component={LogoCB} /></NavPreview>
        </div>

        <p className="mt-12 text-sm text-neutral-500">
          Tip: hover B to see the dot pulse. Reply with A, B, C, D, E, or C+B (or a combo) and I'll wire it through Navbar, Footer, AppShell, AdminShell + favicon.
        </p>
        <p className="mt-2 text-xs text-neutral-400">Preview page {page} — not linked anywhere; remove anytime.</p>
      </div>
    </div>
  );
}
